import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  sites: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const siteSlice = createSlice({
  name: 'site',
  initialState,
  reducers: {
    setSites: (state, action) => {
      state.sites = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setSites, setTotalCount, setTotalPages, setLoading, setError } = siteSlice.actions;

// Get sites (paginated & searchable)
export const getSites = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters?.page || 1;
    const limit = filters?.limit || 10;
    const search = filters?.search || '';
    const response = await axiosInstance.get('/admin/site/list', {
      params: { page, limit, search },
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data?.result || response.data.data || [];
      const totalCount = response.data.data?.totalCount || result.length;
      const totalPages = response.data.data?.totalPages || 1;
      dispatch(setSites(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      dispatch(setSites([]));
      dispatch(setTotalCount(0));
      dispatch(setTotalPages(0));
      const errorMsg = response.data?.message || 'Failed to fetch sites';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    dispatch(setSites([]));
    dispatch(setTotalCount(0));
    dispatch(setTotalPages(0));
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch sites';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get single site details
export const getSiteDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/site/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch site details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch site details';
    return { status: false, message: errorMsg };
  }
};

// Add new site
export const addSite = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/site/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to add site' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to add site';
    return { status: false, message: errorMsg };
  }
};

// Update site details
export const updateSite = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/site/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update site' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update site';
    return { status: false, message: errorMsg };
  }
};

// Delete site
export const deleteSite = (id) => async () => {
  try {
    let response;
    try {
      response = await axiosInstance.delete(`/admin/site/delete/${id}`);
    } catch {
      response = await axiosInstance.delete(`/admin/site/update/${id}`);
    }
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete site' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete site';
    return { status: false, message: errorMsg };
  }
};

export default siteSlice.reducer;
